// KPI modal kabuğu (fork modalı, gösterge seçici): role=dialog + aria-modal + Esc + odak tuzağı —
// ConfirmDialog ile aynı desen, içerik serbest. Kendi düğmelerini children içinde taşır.
// güvenlik/UX taraması 2026-10-08: (U-M2) `dirty` — Esc / ✕ / arka plan tıklaması önce "Kaydedilmemiş
// değişiklikler var" onayı açar (modalın kendi Vazgeç/Kapat düğmeleri bilinçli eylem, kapıdan
// geçmez); (U-M5) açılışta odaklı eleman saklanır, kapanışta odak ona döner.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { KPI_COPY } from "../../lib/kpi-ui";
import { ConfirmDialog } from "../common/ConfirmDialog";

/**
 * Esc bu pencereyi kapatır mı (saf — testle kilitli): onay diyaloğundan (`data-kpi-modal-confirm`)
 * gelen Esc'i o yönetir; bu pencere en üstteki açık `aria-modal` diyalog değilse (ör. üstünde kardeş
 * Kataloğa yükselt penceresi — odak body'de olsa bile) yok sayılır; başka bir diyaloğun alt
 * ağacından gelen Esc de yok sayılır.
 */
export function kpiModalHandlesEscape(args: {
  own: { contains: (node: Node) => boolean } | null;
  target: HTMLElement | null;
  topModal: unknown;
}): boolean {
  const { own, target, topModal } = args;
  if (target?.closest("[data-kpi-modal-confirm]")) return false;
  if (topModal !== null && topModal !== own) return false;
  if (target && own && !own.contains(target) && target.closest('[role="dialog"]')) return false;
  return true;
}

export interface KpiModalProps {
  open: boolean;
  kicker?: string;
  title: string;
  description?: string;
  width?: number;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** kaydedilmemiş değişiklik var → Esc/✕/arka plan kapatmadan önce onay ister */
  dirty?: boolean;
}

export function KpiModal(props: KpiModalProps): JSX.Element | null {
  const { open, kicker, title, description, width = 680, onClose, children, footer } = props;
  const dirty = props.dirty ?? false;
  const titleId = useId();
  const descId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  // onClose çoğu çağrıda satır içi ok fonksiyonu → her üst render'da yeni kimlik. Efekt yalnız
  // `open`'a bağlı kalmalı; aksi halde her tuşta "ilk alana odaklan" yeniden koşar ve yazarken
  // odak etiket alanına kaçar (manuel test bulgusu: anomali eşiğine "50" yazılamıyordu).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  /** Esc / ✕ / arka plan: kirliyse onay, değilse doğrudan kapat */
  function requestClose(): void {
    if (dirtyRef.current) setConfirmClose(true);
    else onCloseRef.current();
  }

  useEffect(() => {
    if (!open) return;
    // U-M5 — açılış odağı saklanır; kapanışta (cleanup) geri verilir
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = ref.current?.querySelector<HTMLElement>(
      'input, select, textarea, button:not([disabled]):not([data-kpi-modal-close]), [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        const topModal = Array.from(
          document.querySelectorAll('[role="dialog"][aria-modal="true"]'),
        ).pop();
        const target = e.target instanceof HTMLElement ? e.target : null;
        if (!kpiModalHandlesEscape({ own: ref.current, target, topModal: topModal ?? null }))
          return;
        e.preventDefault();
        requestClose();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const focusables = ref.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const firstEl = focusables[0]!;
      const lastEl = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      setConfirmClose(false);
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 100,
        overflowY: "auto",
        padding: "48px 16px",
      }}
      onMouseDown={(e) => {
        // yalnız arka planın kendisine tıklama (içerikten kabarcıklanan tıklama değil)
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description ? { "aria-describedby": descId } : {})}
        className="rounded-xl p-6 w-full"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          maxWidth: width,
          fontFamily: "var(--font-sans)",
          position: "relative",
        }}
      >
        <button
          type="button"
          data-kpi-modal-close
          aria-label={KPI_COPY.unsavedClose.closeButton}
          title={KPI_COPY.unsavedClose.closeButton}
          onClick={requestClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: 8,
            border: "1px solid transparent",
            background: "transparent",
            color: "var(--color-text-3)",
            fontSize: "1.1rem",
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>
        {kicker ? (
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: "var(--color-primary)",
              textTransform: "uppercase",
            }}
          >
            {kicker}
          </div>
        ) : null}
        <h2
          id={titleId}
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.2rem",
            marginBottom: description ? 4 : 14,
            color: "var(--color-text)",
            paddingRight: 36,
          }}
        >
          {title}
        </h2>
        {description ? (
          <p
            id={descId}
            className="text-sm"
            style={{ color: "var(--color-text-2)", marginBottom: 14 }}
          >
            {description}
          </p>
        ) : null}
        {children}
        {footer ? (
          <div className="flex items-center gap-2 mt-4" style={{ justifyContent: "flex-end" }}>
            {footer}
          </div>
        ) : null}
        <div data-kpi-modal-confirm>
          <ConfirmDialog
            open={confirmClose}
            title={KPI_COPY.unsavedClose.title}
            body={KPI_COPY.unsavedClose.body}
            confirmLabel={KPI_COPY.unsavedClose.confirm}
            cancelLabel={KPI_COPY.unsavedClose.cancel}
            tone="danger"
            onConfirm={() => {
              setConfirmClose(false);
              onCloseRef.current();
            }}
            onCancel={() => setConfirmClose(false)}
          />
        </div>
      </div>
    </div>
  );
}
